/** Strip a class-invite token out of analytics properties, if one ever
 *  reaches them.
 *
 *  A BACKSTOP, not the mechanism. The token used to ride in the URL path, and
 *  the auth wall copied it into `?next=` on a page that renders, so live
 *  claimable tokens reached `$current_url` and the durable `$initial_*` person
 *  copies. That is fixed at the source: `/invite/<token>` is a route handler
 *  that moves the token into an httpOnly cookie and redirects to a constant
 *  `/join`, so nothing the app produces should reach this hook at all (see
 *  `lib/classes/invite-cookie.ts`).
 *
 *  It stays because it costs nine string compares per event and never drops
 *  one, and because a token in a URL is the kind of mistake that comes back.
 *  If it ever fires, something has regressed.
 *
 *  Matched by ROUTE, not by "a 64-hex run": `%2F` is itself two hex-ish
 *  characters, so a bare hex run starts inside the encoded separator and
 *  redacts an off-by-two window, leaving the token's tail in the clear. That
 *  was a real failure of this file's first draft, caught by the `?next=` case
 *  in the sibling test — keep that case. The hex run comes from
 *  `lib/classes/invite-token.ts`, which owns the shape.
 */
import { INVITE_TOKEN_PATTERN } from "@/lib/classes/invite-token";

/** Both route shapes. `/invite` is what the app emits now; `/join` is what it
 *  emitted mid-branch, before the cookie handoff. Nothing has shipped, so no
 *  `/join` link exists in anyone's inbox — the alternation is eight characters
 *  of insurance against a stale local link, not a live obligation. */
const JOIN_TOKEN_IN_URL = new RegExp(
  `((?:/|%2f)(?:join|invite)(?:/|%2f))${INVITE_TOKEN_PATTERN}`,
  "gi",
);

/** `replace` returns the string unchanged when nothing matches, so this needs
 *  no "does it match?" guard in front of it — and not having one is what keeps
 *  the `/g` regex safe to share. `.test()` on a `/g` regex advances
 *  `lastIndex` and so alternates true/false across calls; `.replace()` does
 *  not. A guard here would have to rebuild the regex per call to dodge that,
 *  which is both the bug and the cost. */
export function redactUrlSecrets(value: string): string {
  return value.replace(JOIN_TOKEN_IN_URL, "$1[redacted]");
}

/** Property keys whose values are URLs. Listing them (rather than walking
 *  every property) keeps the per-event cost at a handful of string checks
 *  and can never mangle an unrelated property. `$set` / `$set_once` carry
 *  the `$initial_*` copies of the same URLs onto the PERSON profile, which
 *  is the longest-lived surface of all. */
const URL_PROPERTY_KEYS = [
  "$current_url",
  "$pathname",
  "$referrer",
  "$initial_current_url",
  "$initial_pathname",
  "$initial_referrer",
  "$session_entry_url",
  "$session_entry_pathname",
  "$session_entry_referrer",
] as const;

function redactBag(bag: unknown): void {
  if (!bag || typeof bag !== "object") return;
  const record = bag as Record<string, unknown>;
  for (const key of URL_PROPERTY_KEYS) {
    const value = record[key];
    if (typeof value === "string") record[key] = redactUrlSecrets(value);
  }
}

/** Mutates and returns the event with every URL-bearing property redacted.
 *  Shaped as a posthog-js `before_send` hook: returning the event keeps it,
 *  returning null would DROP it — this never drops, so it can never cost a
 *  pageview or break the cross-subdomain funnel (CLAUDE.md #42). */
export function redactEventUrlSecrets<
  T extends { properties?: unknown } | null,
>(event: T): T {
  if (!event) return event;
  const properties = event.properties;
  redactBag(properties);
  if (properties && typeof properties === "object") {
    const record = properties as Record<string, unknown>;
    redactBag(record.$set);
    redactBag(record.$set_once);
  }
  return event;
}
